import { useState, useEffect, useCallback } from 'react';
import { faqAPI } from '../services/api';
import FilterBar, { SearchField, FilterSelect, CountPill } from '../components/FilterBar';
import Icon from '../components/Icon';

const s = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  btn: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b' },
  badge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  published: { backgroundColor: '#dcfce7', color: '#166534' },
  draft: { backgroundColor: '#fef3c7', color: '#92400e' },
  iconBtn: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '550px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHead: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFoot: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' },
  error: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
};

const FAQs = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ question: '', answer: '', categoryId: '', isPublished: true, sortOrder: 0 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.categoryId = categoryFilter;
      const res = await faqAPI.getAll(params);
      if (res.data.data) setItems(res.data.data);
    } catch { setError('Failed to fetch FAQs'); }
    finally { setLoading(false); }
  }, [search, statusFilter, categoryFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await faqAPI.getCategories();
      setCategories(res.data.data || []);
    } catch { /* silently ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => { setEditing(null); setFormData({ question: '', answer: '', categoryId: categoryFilter || '', isPublished: true, sortOrder: 0 }); setFormError(''); setShowModal(true); };

  const openEdit = async (item) => {
    try {
      const res = await faqAPI.getById(item.id);
      const d = res.data;
      setEditing(d);
      setFormData({ question: d.question || '', answer: d.answer || '', categoryId: d.categoryId ? String(d.categoryId) : '', isPublished: d.isPublished, sortOrder: d.sortOrder || 0 });
      setFormError(''); setShowModal(true);
    } catch { setError('Failed to load FAQ'); }
  };

  const handleSubmit = async () => {
    if (!formData.question || !formData.answer) { setFormError('Question and answer are required'); return; }
    try {
      setSaving(true); setFormError('');
      if (editing) await faqAPI.update(editing.id, formData);
      else await faqAPI.create(formData);
      setShowModal(false); fetchItems();
    } catch (err) { setFormError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await faqAPI.delete(id); setDeleteConfirm(null); fetchItems(); }
    catch { setError('Failed to delete'); }
  };

  const handleToggle = async (id) => {
    try { await faqAPI.togglePublish(id); fetchItems(); }
    catch { setError('Failed to toggle'); }
  };

  if (loading && items.length === 0) return <div style={s.container}><p style={{ textAlign: 'center', color: '#64748b', padding: '40px' }}>Loading...</p></div>;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>FAQs</h1>
          <p style={s.subtitle}>Manage frequently asked questions</p>
        </div>
        <button style={s.btn} onClick={openCreate}>+ Add FAQ</button>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.card}>
        <FilterBar right={<CountPill count={items.length} noun="FAQ" nounPlural="FAQs" />}>
          <SearchField value={search} onChange={setSearch} placeholder="Search FAQs..." />
          <FilterSelect value={categoryFilter} onChange={setCategoryFilter} aria-label="Category">
            <option value="">All Categories</option>
            <option value="null">Uncategorized</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </FilterSelect>
          <FilterSelect value={statusFilter} onChange={setStatusFilter} aria-label="Status">
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </FilterSelect>
        </FilterBar>

        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
            <div style={{ marginBottom: '16px', color: '#cbd5e1', display: 'flex', justifyContent: 'center' }}><Icon name="help" size={44} /></div>
            <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>No FAQs found</div>
            <p>Click "+ Add FAQ" to create your first question.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Order</th>
                  <th style={s.th}>Question</th>
                  <th style={s.th}>Category</th>
                  <th style={s.th}>Answer</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={s.td}>{item.sortOrder}</td>
                    <td style={{ ...s.td, fontWeight: '500', maxWidth: '280px' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.question}</div>
                    </td>
                    <td style={s.td}>
                      {item.category ? (
                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#6366f1', background: '#eef2ff', padding: '3px 8px', borderRadius: '6px' }}>{item.category.name}</span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...s.td, maxWidth: '280px', color: '#64748b' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.answer}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, ...(item.isPublished ? s.published : s.draft) }} onClick={() => handleToggle(item.id)}>
                        {item.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button style={s.iconBtn} onClick={() => openEdit(item)} title="Edit">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button style={{ ...s.iconBtn, color: '#ef4444' }} onClick={() => setDeleteConfirm(item)} title="Delete">
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={s.modal} onClick={() => setShowModal(false)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>{editing ? 'Edit FAQ' : 'Add FAQ'}</span>
              <button style={{ ...s.iconBtn, fontSize: '20px' }} onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              {formError && <div style={s.error}>{formError}</div>}
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Question *</label>
                <input style={s.input} placeholder="Enter the question" value={formData.question} onChange={(e) => setFormData({ ...formData, question: e.target.value })} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Answer *</label>
                <textarea style={{ ...s.textarea, minHeight: '120px' }} placeholder="Enter the answer" value={formData.answer} onChange={(e) => setFormData({ ...formData, answer: e.target.value })} rows={5} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={s.label}>Category</label>
                <select
                  style={{ ...s.input, backgroundColor: '#fff' }}
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                >
                  <option value="">Uncategorized</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                  Select the page category where this FAQ should appear (home, bike-insurance, car-insurance, etc.).
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={s.label}>Sort Order</label>
                  <input style={s.input} type="number" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })} />
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={formData.isPublished} onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })} />
                    <span style={{ fontSize: '14px', color: '#475569' }}>Published</span>
                  </label>
                </div>
              </div>
            </div>
            <div style={s.modalFoot}>
              <button style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none', backgroundColor: '#f1f5f9', color: '#475569' }} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={{ ...s.btn, opacity: saving ? 0.7 : 1 }} onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div style={s.modal} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modalBox, maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHead}>
              <span style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>Delete FAQ</span>
              <button style={{ ...s.iconBtn, fontSize: '20px' }} onClick={() => setDeleteConfirm(null)}>&times;</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>Delete this FAQ? This cannot be undone.</p>
            </div>
            <div style={s.modalFoot}>
              <button style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none', backgroundColor: '#f1f5f9', color: '#475569' }} onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none', backgroundColor: '#ef4444', color: '#fff' }} onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQs;
